import React, { useCallback } from 'react';
import { useRedirect } from 'react-admin';
import { Box, Typography, Tooltip, Chip, styled } from '@mui/material';
import { ChevronRight, Link as LinkIcon } from '@mui/icons-material';

const StyledChip = styled(Chip)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '& .MuiChip-icon': {
    color: theme.palette.primary.contrastText,
  },
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
  },
}));

export interface EntityLinkProps<T = any> {
  record: T;
  idField: string;
  basePath: string;
  tooltipText?: string;
  emptyText?: string;
  chipProps?: object;
  labelField?: string;
}

const EntityLink = <T extends Record<string, any>>({
  record,
  idField,
  basePath,
  tooltipText = "Перейти к записи",
  emptyText = "—",
  chipProps = {},
  labelField,
}: EntityLinkProps<T>): React.ReactElement => {
  const redirect = useRedirect();

  const id = record?.[idField];
  const label = labelField && record?.[labelField];
  
  const handleRedirect = useCallback(() => {
    if (id) {
      redirect(`${basePath}/${id}/show`);
    }
  }, [id, basePath, redirect]);

  if (!id) {
    return <Typography variant="body2" color="textSecondary">{emptyText}</Typography>;
  }

  return (
    <Box display="flex" alignItems="center">
      <Tooltip title={tooltipText} arrow placement="top">
        <StyledChip
          label={label || id}
          onClick={handleRedirect}
          icon={<LinkIcon fontSize="small" />}
          deleteIcon={<ChevronRight />}
          onDelete={handleRedirect}
          {...chipProps}
        />
      </Tooltip>
    </Box>
  );
};

export default EntityLink; 